import { NavLink } from 'react-router-dom';
import type { Environment, MeResponse, Permission } from '../../api/users';
import SearchBox from '../SearchBox';
import ThemeToggle from '../ThemeToggle';
import { UserMenu } from './UserMenu';

const ENVIRONMENT_LABELS: Record<Environment, string> = {
  PRODUCTION: 'Production',
  STAGING: 'Staging',
  DEVELOPMENT: 'Development',
  UNKNOWN: 'Unknown',
};

export interface TopBannerProps {
  /** The `/me` answer, undefined until it loads (or when it fails). */
  me: MeResponse | undefined;
  userEmail: string | null;
  onLogout: () => void;
  /** Slug of the wiki sub-project the current route is inside, or null outside one. */
  wikiSlug: string | null;
  /** Whether to show the wiki's LLM search box. */
  showSearch: boolean;
}

function hasPermission(me: MeResponse | undefined, permission: Permission): boolean {
  return me?.permissions.includes(permission) ?? false;
}

/**
 * The top bar: brand, the wiki nav and search when inside a wiki, the theme toggle, the environment `/me` reports and
 * the [UserMenu], which holds Admin, Users and Logout.
 */
export function TopBanner({ me, userEmail, onLogout, wikiSlug, showSearch }: TopBannerProps) {
  return (
    <header className="top-bar" data-testid="main-header">
      <NavLink to="/" className="top-bar__brand" data-testid="nav-home">
        bevi-ailab
      </NavLink>
      <nav className="top-bar__nav" data-testid="main-nav">
        {wikiSlug ? (
          <>
            <NavLink
              to={`/${wikiSlug}/wiki/index`}
              data-testid="nav-wiki"
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              wiki
            </NavLink>
            <NavLink
              to={`/${wikiSlug}/raw`}
              data-testid="nav-raw"
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              raw
            </NavLink>
            <NavLink
              to={`/${wikiSlug}/updates`}
              data-testid="nav-updates"
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              updates
            </NavLink>
          </>
        ) : null}
      </nav>
      <div className="top-bar__search">{showSearch ? <SearchBox /> : null}</div>
      <div className="top-bar__spacer" />
      <div className="top-bar__user">
        <ThemeToggle />
        {me ? (
          <span className="top-bar__environment" data-testid="environment">
            {ENVIRONMENT_LABELS[me.environment]}
          </span>
        ) : null}
        <UserMenu
          email={me?.email ?? userEmail}
          showAdmin={hasPermission(me, 'VIEW_ADMIN')}
          showUsers={hasPermission(me, 'VIEW_USERS')}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
}
